import type { User, PublicUser } from '@cibli/shared';

import { DEFAULT_LANGUAGE } from '@cibli/shared';
import * as Sentry from '@sentry/react';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

import type { UserInfo } from '@/features/register/context/register.context';

import { useAuth } from '@/features/auth/contexts/auth.context';
import { signOut } from '@/lib/auth-client';
import { setApiLanguage } from '@/services/api';
import { clearOtpCache } from '@/services/otp';
import { createUser, getCurrentUser } from '@/services/user';

export type AppUser = User | PublicUser;

interface UserContextState {
  user: AppUser | null;
  /** If userInfos is provided: create user and set in context. If not: use existing session (isAuthenticated) and sync user from API. */
  login: (userInfos?: UserInfo) => Promise<boolean>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextState | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
  /** Called after clearing user state on logout (e.g. clear interview, reset API language). */
  onLogout?: () => void;
}

export function UserProvider({ children, onLogout }: UserProviderProps) {
  const [user, setUser] = useState<AppUser | null>(null);
  const { refetch: refetchSession } = useAuth();

  const setUserSafe = useCallback((u: AppUser | null) => {
    setUser(u);
    Sentry.setUser(
      u
        ? {
            id: u.id,
            email: 'email' in u && u.email ? String(u.email) : undefined,
          }
        : null,
    );
  }, []);

  const login = useCallback(
    async (userInfos?: UserInfo): Promise<boolean> => {
      if (userInfos !== undefined) {
        const newUser = await createUser(userInfos, { showErrorToast: false });
        setUserSafe(newUser);
        return true;
      }
      // After OTP verification, session cookie is set but isAuthenticated may not have updated yet
      // (stale closure). So we always try getCurrentUser(); it will succeed if the session is valid.
      try {
        const existingUser = await getCurrentUser({ showErrorToast: false });
        if (!existingUser?.id) {
          throw new Error('No user returned from /users/me');
        }
        setUserSafe(existingUser);
        return true;
      } catch {
        return false;
      }
    },
    [setUserSafe],
  );

  const logout = useCallback(async () => {
    setApiLanguage(DEFAULT_LANGUAGE);

    try {
      await signOut();
      await refetchSession();
    } catch (err) {
      console.error('Failed to sign out', err);
    }

    if (user?.phoneNumber) {
      try {
        await clearOtpCache(user.phoneNumber, false);
      } catch (err) {
        console.error('Failed to clear OTP cache on logout', err);
      }
    }

    setUserSafe(null);
    onLogout?.();
  }, [user?.phoneNumber, setUserSafe, refetchSession, onLogout]);

  const value: UserContextState = {
    user,
    login,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserContext(): UserContextState {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
